import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import * as NDJson from '@app/_v3/services/ndj.lib';
import { AuthService } from '@core/services/lingo2-account/auth.service';
import { environment } from '@env/environment';
import { BffGetContentEndpoint } from 'lingo2-api-models';
import { EContentPanelRoute, IPagedResults, FindContentStrategyEnum } from 'lingo2-models';
import { map } from 'rxjs';
import { HeaderBffDataVer } from '../bff.service';
import { ContextService } from '../context.service';
import { FeaturesService } from '../features.service';
import { LANGUAGE_LOCAL_STORAGE_KEY } from '../language.service';

export const AVAILABLE_BFF_V2_ROUTES: EContentPanelRoute[] = [EContentPanelRoute.classes, EContentPanelRoute.tutors];

/**
 * TODO. Исправить. Будет использоваться пока получение данных не будет полностью на стримах
 * Список роутов которые работают через стримы
 * и которым не нужен запрос данных из pane.component
 **/
export const STREAM_ROUTES: EContentPanelRoute[] = [
  EContentPanelRoute.classes,
  EContentPanelRoute.tutors,
  EContentPanelRoute.lessons,
  EContentPanelRoute.collections,
  EContentPanelRoute.schools,
  EContentPanelRoute.main,
];

@Injectable({ providedIn: 'root' })
export class BffV2Service {
  private readonly http = inject(HttpClient);
  private authService = inject(AuthService);
  private featuresService = inject(FeaturesService);
  private readonly baseUrl = `${environment.bff_url}/api/v2/`;
  private readonly contextService = inject(ContextService);
  private abortController = new AbortController();

  /*
   */
  public async abort(): Promise<void> {
    return new Promise((rv, rj) => {
      setTimeout(() => {
        this.abortController?.abort();
        this.abortController = new AbortController();
        return rv();
      }, 1);
    });
  }

  getContent(body: BffGetContentEndpoint.Body) {
    return this.http.post<BffGetContentEndpoint.Response>(`${this.baseUrl}${BffGetContentEndpoint.path}`, body).pipe(
      map((response) => {
        if (response.status === 'ok') {
          return response.data;
        } else {
          // TODO: handle error
          console.error(response.errorMessage);
          return [];
        }
      }),
    );
  }

  async *getContentStream(body: BffGetContentEndpoint.Body) {
    const url = `${this.baseUrl}${BffGetContentEndpoint.path}`;
    const headers = this.prepareHeaders();

    try {
      const res = await NDJson.ndj_fetch(url, body, this.abortController.signal, headers);
      const version = res.headers.get(HeaderBffDataVer);

      for await (const item of NDJson.ndj_parse_stream(res.body, this.abortController?.signal)) {
        yield { item, cache_key: version };
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        throw err;
      }
      console.log('Fetch aborted');
    }
  }

  private prepareHeaders() {
    const currency = this.featuresService.portalCurrency;

    return {
      Authorization: 'Bearer ' + this.authService.accessToken,
      ['x-portal-language']: localStorage.getItem(LANGUAGE_LOCAL_STORAGE_KEY),
      ['x-portal-currency']: currency.toString(),
      ['x-debug']: this.contextService.debug,
    };
  }
}

// ...
export const getStrategyPayload = (filter: any, strategy: FindContentStrategyEnum, opts: object = {}) => ({
  strategy,
  filter: {
    ...opts,
    ...filter,
  },
});

/** TODO! Temp Helpers for compat. */
export const map_to_legacy = (res: any): IPagedResults<any[]> => {
  const raw = Array.isArray(res?.results) ? res.results : Array.isArray(res?.list) ? res.list : [];

  return {
    results: raw.map((x: any) => ({ ...x })),
    pagination: res?.pagination ?? {},
    ...res?.pagination,
  };
};
/** TODO! Temp Helpers for compat. */
// Todo. Temp solution. Not sure we can use IMAGES URL
// in templates now, because some templates used for different
// type of response for images fields.
export const map_images = (res: IPagedResults<any[]>): IPagedResults<any[]> => {
  const PREFIX = environment.files_storage_host;
  const TARGET_KEYS = new Set(['thumb', 'wideX2', 'lgX2', 'wide', 'mdX2', 'smX2', 'sm', 'lg', 'md', 'original']);

  function addPrefixToUrlsImmutable(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map(addPrefixToUrlsImmutable);
    }
    // Только plain object
    if (obj && typeof obj === 'object' && obj.constructor === Object) {
      const result: Record<string, any> = {};
      for (const key of Object.keys(obj)) {
        const value = obj[key];
        if (TARGET_KEYS.has(key) && value?.url) {
          result[key] = { ...value, url: PREFIX + value.url };
        } else {
          result[key] = addPrefixToUrlsImmutable(value);
        }
      }
      return result;
    }
    // Для всех остальных типов (строка, число, дата и т.д.) — вернуть как есть
    return obj;
  }

  if (!res || typeof res !== 'object') return res;

  return {
    ...res,
    results: Array.isArray(res.results)
      ? res.results.map((item) => (item && typeof item === 'object' ? addPrefixToUrlsImmutable(item) : item))
      : res.results,
  };
};
